import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Carousel, Collapse, Drawer, Input, Spin, Tabs, Tag } from "antd";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { apiRequest } from "util/services";
import {
  WELCOME_REVIEWS_DRAWER_SHARED_PROPS,
  welcomeReviewsDrawerWidth,
} from "pages/Welcome/welcomeReviewsDrawerProps";
import styles from "./emergentCandidates.module.scss";

const { TextArea } = Input;

const parseCreatedAtMs = (value) => {
  const ms = Date.parse(value || "");
  return Number.isFinite(ms) ? ms : 0;
};

const groupCandidatesByRun = (candidates) => {
  if (!candidates.length) {
    return { current: [], previous: [], latestRunAtMs: 0, latestRunId: null };
  }

  const byRun = candidates.reduce((acc, candidate) => {
    const runId = candidate.scan_run_id || "unknown";
    if (!acc[runId]) acc[runId] = [];
    acc[runId].push(candidate);
    return acc;
  }, {});

  let latestRunId = null;
  let latestRunAtMs = 0;
  Object.entries(byRun).forEach(([runId, rows]) => {
    const runMax = Math.max(...rows.map((row) => parseCreatedAtMs(row.created_at)));
    if (runMax >= latestRunAtMs) {
      latestRunAtMs = runMax;
      latestRunId = runId;
    }
  });

  const sortRows = (rows) =>
    [...rows].sort((a, b) => {
      const supportDiff = (b.support_count || 0) - (a.support_count || 0);
      if (supportDiff !== 0) return supportDiff;
      return (a.id || 0) - (b.id || 0);
    });

  const current = sortRows(byRun[latestRunId] || []);
  const previous = sortRows(
    candidates.filter((candidate) => candidate.scan_run_id !== latestRunId)
  );

  return { current, previous, latestRunAtMs, latestRunId };
};

const EmergentCandidatesPanel = ({ dashboardId, autoExpand = false }) => {
  const { t, i18n } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("current");
  const [expandedKey, setExpandedKey] = useState(null);
  const [reasons, setReasons] = useState({});
  const [submittingId, setSubmittingId] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsCandidate, setReviewsCandidate] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [expandedSiblingIds, setExpandedSiblingIds] = useState(() => new Set());

  const toggleSiblingPanel = useCallback((candidateId) => {
    setExpandedSiblingIds((prev) => {
      const next = new Set(prev);
      const key = String(candidateId);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadCandidates = useCallback(async () => {
    if (!dashboardId) return;
    setLoading(true);
    const res = await apiRequest(
      `emergent-topics/${dashboardId}/candidates`,
      "GET",
      null,
      true
    );
    const rows = res?.candidates || [];
    setCandidates(rows);
    if (autoExpand && rows.length) {
      setExpandedKey(String(rows[0].id));
    }
    setLoading(false);
  }, [dashboardId, autoExpand]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const { current, previous, latestRunAtMs } = useMemo(
    () => groupCandidatesByRun(candidates),
    [candidates]
  );

  const tabCandidates = activeTab === "current" ? current : previous;

  const formatDiscoveredAt = useCallback(
    (value) => {
      const ms = parseCreatedAtMs(value);
      if (!ms) return "";
      return new Date(ms).toLocaleString(i18n.language || "tr-TR", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [i18n.language]
  );

  const openReviews = async (candidate) => {
    setReviewsCandidate(candidate);
    setReviewsOpen(true);
    setReviewsLoading(true);
    setReviews([]);
    const res = await apiRequest(
      `emergent-topics/candidates/${candidate.id}/reviews?dashboard_id=${dashboardId}`,
      "GET",
      null,
      true
    );
    setReviews(res?.reviews || []);
    setReviewsLoading(false);
  };

  const decide = async (candidate, decision) => {
    setSubmittingId(candidate.id);
    const res = await apiRequest(
      `emergent-topics/candidates/${candidate.id}/decide`,
      "POST",
      {
        dashboard_id: Number(dashboardId),
        decision,
        reason: reasons[candidate.id] || "",
      },
      true
    );
    setSubmittingId(null);
    if (res?.success) {
      setCandidates((prev) => prev.filter((c) => c.id !== candidate.id));
      setExpandedKey(null);
    }
  };

  const renderClosest = (candidate) => {
    if (!candidate.closest_topic_label) return null;
    const siblings = candidate.sibling_topics || [];
    const siblingTotal =
      candidate.sibling_topics_total != null
        ? candidate.sibling_topics_total
        : siblings.length;
    const parentLabel =
      candidate.parent_topic_label ||
      (candidate.closest_topic_label.includes(" > ")
        ? candidate.closest_topic_label.split(" > ")[0].trim()
        : null);
    const panelOpen = expandedSiblingIds.has(String(candidate.id));
    const showSiblingPanel = siblingTotal > 0 && parentLabel;

    return (
      <div className={styles.closestBlock}>
        <div className={styles.closestLine}>
          {t("dashboard.emergent_closest_topic_short", {
            defaultValue: "En yakın mevcut konu: {{topic}}",
            topic: candidate.closest_topic_label,
          })}
        </div>
        {showSiblingPanel ? (
          <>
            <button
              type="button"
              className={styles.parentToggle}
              onClick={() => toggleSiblingPanel(candidate.id)}
              aria-expanded={panelOpen}
            >
              <span
                className={`${styles.parentToggleChev} ${
                  panelOpen ? styles.parentToggleChevOpen : ""
                }`}
                aria-hidden
              >
                ▶
              </span>
              {t("dashboard.emergent_sibling_topics_toggle", {
                defaultValue: "{{parent}} altındaki diğer konular ({{count}})",
                parent: parentLabel,
                count: siblingTotal,
              })}
            </button>
            {panelOpen ? (
              <div className={styles.siblingPanel}>
                <div className={styles.siblingTitle}>
                  {t("dashboard.emergent_sibling_topics_hint", {
                    defaultValue: "Aynı üst konu altında · kaydırarak inceleyin",
                  })}
                </div>
                <div className={styles.siblingListScroll}>
                  <ul className={styles.siblingList}>
                    {siblings.map((sibling) => (
                      <li
                        key={sibling.topic_id}
                        className={`${styles.siblingItem} ${
                          sibling.is_closest ? styles.siblingItemClosest : ""
                        }`}
                      >
                        <span className={styles.siblingItemLabel}>{sibling.label}</span>
                        {sibling.is_closest ? (
                          <span className={styles.siblingClosestPill}>
                            {t("dashboard.emergent_sibling_closest_pill", {
                              defaultValue: "En yakın",
                            })}
                          </span>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  };

  const renderDiscovered = (candidate) => {
    const label = formatDiscoveredAt(candidate.created_at);
    if (!label) return null;
    return (
      <div className={styles.discoveredAt}>
        {t("dashboard.emergent_discovered_at", {
          defaultValue: "Keşfedildi: {{date}}",
          date: label,
        })}
      </div>
    );
  };

  const renderKeywords = (candidate) => {
    const kws = candidate.keywords || [];
    if (!kws.length) return null;
    return (
      <div className={styles.keywords}>
        {kws.slice(0, 6).map((kw) => (
          <Tag key={kw} className={styles.keywordTag}>
            {kw}
          </Tag>
        ))}
      </div>
    );
  };

  const renderDecisionForm = (candidate) => (
    <div className={styles.decisionBlock}>
      <label className={styles.reasonLabel} htmlFor={`reason-${candidate.id}`}>
        {t("dashboard.emergent_why_optional", { defaultValue: "Neden? (isteğe bağlı)" })}
      </label>
      <TextArea
        id={`reason-${candidate.id}`}
        rows={2}
        placeholder={t("dashboard.emergent_why_placeholder", {
          defaultValue: "ör. Zaten Resepsiyon altında kapsanıyor",
        })}
        value={reasons[candidate.id] || ""}
        onChange={(e) =>
          setReasons((prev) => ({ ...prev, [candidate.id]: e.target.value }))
        }
        className={styles.reasonInput}
      />
      <div className={styles.actions}>
        <Button
          icon={<CloseOutlined />}
          onClick={() => decide(candidate, "reject")}
          loading={submittingId === candidate.id}
        >
          {t("dashboard.emergent_reject", { defaultValue: "Hayır, bu değil" })}
        </Button>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => decide(candidate, "accept")}
          loading={submittingId === candidate.id}
        >
          {t("dashboard.emergent_accept", { defaultValue: "Evet, konu ekle" })}
        </Button>
      </div>
    </div>
  );

  const renderCardBody = (candidate) => (
    <>
      {renderDiscovered(candidate)}
      {candidate.description ? (
        <p className={styles.reason}>{candidate.description}</p>
      ) : null}
      {renderKeywords(candidate)}
      {renderClosest(candidate)}
      {renderDecisionForm(candidate)}
    </>
  );

  const renderPanelHeader = (candidate, { stopPropagation = false } = {}) => (
    <div className={styles.panelHeader}>
      <Tag className={styles.badge}>
        {t("dashboard.emergent_candidate_badge", { defaultValue: "Aday" })}
      </Tag>
      <span className={styles.panelTitle}>{candidate.label}</span>
      <button
        type="button"
        className={styles.reviewCountBtn}
        onClick={(e) => {
          if (stopPropagation) e.stopPropagation();
          openReviews(candidate);
        }}
      >
        {t("dashboard.emergent_review_count", {
          defaultValue: "{{count}} yorum",
          count: candidate.support_count || 0,
        })}
      </button>
    </div>
  );

  const renderAccordion = (rows) => (
    <Collapse
      accordion
      activeKey={expandedKey}
      onChange={(key) => setExpandedKey(key)}
      className={styles.accordion}
      items={rows.map((candidate) => ({
        key: String(candidate.id),
        label: renderPanelHeader(candidate, { stopPropagation: true }),
        children: renderCardBody(candidate),
      }))}
    />
  );

  const renderCarousel = (rows) => (
    <div className={styles.carouselWrap}>
      <Carousel dots afterChange={setCarouselIndex} className={styles.carousel}>
        {rows.map((candidate) => (
          <div key={candidate.id}>
            <div
              className={`${styles.carouselCard} ${
                expandedKey === String(candidate.id) ? styles.carouselCardOpen : ""
              }`}
            >
              <div className={styles.panelHeader}>
                <Tag className={styles.badge}>
                  {t("dashboard.emergent_candidate_badge", {
                    defaultValue: "Aday",
                  })}
                </Tag>
                <button
                  type="button"
                  className={styles.panelTitleBtn}
                  onClick={() =>
                    setExpandedKey((k) =>
                      k === String(candidate.id) ? null : String(candidate.id)
                    )
                  }
                >
                  {candidate.label}
                </button>
                <button
                  type="button"
                  className={styles.reviewCountBtn}
                  onClick={() => openReviews(candidate)}
                >
                  {t("dashboard.emergent_review_count", {
                    defaultValue: "{{count}} yorum",
                    count: candidate.support_count || 0,
                  })}
                </button>
              </div>
              {expandedKey === String(candidate.id) ? renderCardBody(candidate) : null}
            </div>
          </div>
        ))}
      </Carousel>
      <div className={styles.carouselMeta}>
        {t("dashboard.emergent_carousel_index", {
          defaultValue: "{{total}} adaydan {{current}}",
          current: carouselIndex + 1,
          total: rows.length,
        })}
      </div>
    </div>
  );

  const renderCandidateList = (rows) => {
    if (!rows.length) {
      return (
        <p className={styles.emptyTab}>
          {activeTab === "current"
            ? t("dashboard.emergent_empty_current", {
                defaultValue: "Bu taramada yeni aday yok.",
              })
            : t("dashboard.emergent_empty_previous", {
                defaultValue: "Önceki taramalardan bekleyen aday yok.",
              })}
        </p>
      );
    }
    return rows.length > 3 ? renderCarousel(rows) : renderAccordion(rows);
  };

  const runMeta = useMemo(() => {
    if (activeTab !== "current" || !current.length || !latestRunAtMs) return "";
    return t("dashboard.emergent_run_meta", {
      defaultValue: "Son tarama: {{date}} · {{count}} yeni aday",
      date: formatDiscoveredAt(new Date(latestRunAtMs).toISOString()),
      count: current.length,
    });
  }, [activeTab, current.length, formatDiscoveredAt, latestRunAtMs, t]);

  const tabItems = useMemo(
    () => [
      {
        key: "current",
        label: t("dashboard.emergent_tab_current", {
          defaultValue: "Bu taramada ({{count}})",
          count: current.length,
        }),
      },
      {
        key: "previous",
        label: t("dashboard.emergent_tab_previous", {
          defaultValue: "Önceki taramalar ({{count}})",
          count: previous.length,
        }),
      },
    ],
    [current.length, previous.length, t]
  );

  if (loading) {
    return (
      <div className={styles.wrap}>
        <Spin />
      </div>
    );
  }

  if (!candidates.length) {
    return null;
  }

  return (
    <>
      <section className={styles.wrap} id="emergent-candidates-panel">
        <div className={styles.header}>
          <h3 className={styles.title}>
            {t("dashboard.emergent_suggested_title", {
              defaultValue: "Yükselişte olan yeni konu keşifleri",
            })}
          </h3>
          <p className={styles.subtitle}>
            {t("dashboard.emergent_subtitle", {
              defaultValue:
                "Aday konuları tarama oturumuna göre inceleyin · Geri bildiriminiz gelecek önerileri iyileştirir",
            })}
          </p>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setExpandedKey(null);
            setCarouselIndex(0);
          }}
          items={tabItems}
          className={styles.tabs}
        />
        {runMeta ? <p className={styles.runMeta}>{runMeta}</p> : null}
        {renderCandidateList(tabCandidates)}
      </section>

      <Drawer
        {...WELCOME_REVIEWS_DRAWER_SHARED_PROPS}
        title={
          reviewsCandidate?.label ||
          t("dashboard.emergent_reviews_title", { defaultValue: "Destekleyen yorumlar" })
        }
        open={reviewsOpen}
        onClose={() => setReviewsOpen(false)}
        width={welcomeReviewsDrawerWidth()}
        extra={
          <button
            type="button"
            className="welcome-reviews-drawer-header-close"
            onClick={() => setReviewsOpen(false)}
          >
            {t("common.close", "Close")}
          </button>
        }
      >
        {reviewsLoading ? (
          <Spin />
        ) : (
          <ul className={styles.reviewList}>
            {reviews.map((row) => (
              <li key={row.date_gpid || row.review_id} className={styles.reviewItem}>
                <div className={styles.reviewMeta}>
                  {row.rating != null ? (
                    <span className={styles.reviewRating}>{row.rating}★</span>
                  ) : null}
                  <span className={styles.reviewId}>{row.date_gpid}</span>
                </div>
                <p className={styles.reviewText}>{row.text}</p>
              </li>
            ))}
            {!reviews.length ? (
              <p className={styles.reviewEmpty}>
                {t("dashboard.emergent_reviews_empty", {
                  defaultValue: "Bu aday için yorum bulunamadı.",
                })}
              </p>
            ) : null}
          </ul>
        )}
      </Drawer>
    </>
  );
};

export default EmergentCandidatesPanel;
